/**
 * coup-de-pouce-content.ts
 * ─────────────────────────────────────────────────────────────────
 * Source unique du contenu éditorial de la page-guide
 * /particuliers/aides/coup-de-pouce.
 *
 * RÈGLES ÉDITORIALES (AGENTS §8) :
 *  - Aucune mention "CEE Coup de Pouce Copro" (n'existe pas
 *    officiellement). Pour les copros : "primes collectives".
 *  - Aucun montant en € précis (les bonifications évoluent).
 *  - Les Coups de pouce sont des majorations de la prime énergie.
 */

// ════════════════════════════════════════════════════════════════
// TYPES
// ════════════════════════════════════════════════════════════════

export interface CoupSummaryPoint {
  text: string;
}

export interface CoupOperation {
  /** Nom du Coup de pouce */
  title: string;
  iconName: string;
  /** Sous-titre */
  caption: string;
  /** Description */
  description: string;
  /** Types d'opérations ciblées */
  operations: string[];
  /** Note ou éligibilité particulière */
  note?: string;
}

export interface CoupBareme {
  /** Profil de revenus */
  profil: string;
  color: 'bleu' | 'jaune' | 'violet' | 'rose';
  /** Description du barème majoré */
  description: string;
}

export interface CoupCondition {
  iconName: string;
  title: string;
  description: string;
}

export interface CoupDemarche {
  step: number;
  iconName: string;
  title: string;
  description: string;
}

export interface CoupCumul {
  aide: string;
  iconName: string;
  description: string;
  tone: 'brand' | 'accent' | 'sage' | 'lavender' | 'ivory';
  href?: string;
}

export interface CoupFaqItem {
  question: string;
  answer: string;
}

// ════════════════════════════════════════════════════════════════
// HERO
// ════════════════════════════════════════════════════════════════

export const COUP_HERO = {
  eyebrow: 'Prime majorée',
  titlePrimary: 'Coup de pouce',
  titleAccent: 'la prime énergie renforcée',
  lead:
    "Le « Coup de pouce » majore la prime énergie classique sur certaines opérations prioritaires : remplacement d'un chauffage fossile, isolation, rénovation performante. Le montant est plus élevé, parfois doublé. Surtout pour les ménages aux revenus modestes.",
  amountHeadline: 'Prime majorée',
  amountSubline:
    "Le Coup de pouce s'ajoute à la prime énergie de base. Cumulable avec l'aide de l'État.",
  microPromise:
    'Encadré par charte qualité. Montant selon vos revenus et vos travaux.',
} as const;

export const COUP_SUMMARY: CoupSummaryPoint[] = [
  {
    text:
      "Le Coup de pouce n'est pas une aide autonome : c'est une majoration de la prime énergie sur certaines opérations ciblées.",
  },
  {
    text:
      "La majoration est définie par arrêté. Les opérations éligibles et les montants évoluent régulièrement selon les priorités énergétiques nationales.",
  },
  {
    text:
      "Le bonus est plus généreux pour les ménages aux revenus modestes ou très modestes.",
  },
  {
    text:
      "Versé par un partenaire signataire de la charte qualité officielle (fournisseur d'énergie ou intermédiaire agréé).",
  },
];

// ════════════════════════════════════════════════════════════════
// MÉCANISME
// ════════════════════════════════════════════════════════════════

export const COUP_MECANISME = {
  eyebrow: 'Le mécanisme',
  title: 'Une prime majorée, encadrée par une charte qualité',
  description:
    "Sur les opérations identifiées comme prioritaires, l'État met en place le dispositif « Coup de pouce ». Concrètement, les acteurs de la prime énergie s'engagent volontairement à signer la charte Coup de pouce correspondante. En contrepartie, ils versent des primes majorées par rapport au tarif classique.",
} as const;

// ════════════════════════════════════════════════════════════════
// LES COUPS DE POUCE
// ════════════════════════════════════════════════════════════════

export const COUP_OPERATIONS: CoupOperation[] = [
  {
    title: 'Coup de pouce Chauffage',
    iconName: 'flame',
    caption: "Remplacement d'un chauffage fossile",
    description:
      "Prime majorée pour remplacer une chaudière fioul, charbon ou gaz par un équipement plus performant et décarboné. C'est le dispositif historique, mis en place en 2019 pour accélérer la sortie des énergies fossiles.",
    operations: [
      'Pompe à chaleur air-eau / géothermique / hybride',
      'Chaudière biomasse performante',
      'Système solaire combiné',
      'Raccordement à un réseau de chaleur renouvelable',
      'Chauffage au bois performant (poêle à granulés)',
    ],
  },
  {
    title: 'Coup de pouce Isolation',
    iconName: 'snowflake',
    caption: 'Combles, toitures et planchers bas',
    description:
      "Prime majorée pour l'isolation des combles perdus, des rampants de toiture et des planchers bas. Vise les déperditions thermiques les plus coûteuses (jusqu'à 30 % par la toiture).",
    operations: [
      'Isolation des combles perdus',
      'Isolation des rampants de toiture',
      'Isolation des planchers bas (caves, vides sanitaires, garages)',
    ],
    note:
      "Depuis 2026, l'isolation des murs n'est plus éligible en geste isolé pour l'aide de l'État, mais elle reste accessible aux primes énergie classiques.",
  },
  {
    title: 'Coup de pouce Rénovation performante',
    iconName: 'layers',
    caption: 'Rénovation globale avec gain énergétique',
    description:
      "Prime majorée qui s'applique aux rénovations globales avec un gain énergétique conséquent. Cumulable avec l'aide de l'État pour les rénovations d'ampleur, pour aller chercher le maximum d'aides cumulées.",
    operations: [
      'Bouquet de travaux mixant chauffage et isolation',
      "Gain énergétique d'au moins 35 % requis",
      'Diagnostic énergétique préalable',
    ],
  },
];

// ════════════════════════════════════════════════════════════════
// BARÈMES selon profils de revenus
// ════════════════════════════════════════════════════════════════

export const COUP_BAREMES: CoupBareme[] = [
  {
    profil: 'Très modestes',
    color: 'bleu',
    description:
      "Majoration maximale. Sur certaines opérations (remplacement chauffage fossile), la prime peut couvrir une part très importante du coût des travaux.",
  },
  {
    profil: 'Modestes',
    color: 'jaune',
    description:
      "Majoration importante, légèrement minorée par rapport au profil Très modestes. Reste très avantageuse.",
  },
  {
    profil: 'Intermédiaires',
    color: 'violet',
    description:
      "Majoration standard du dispositif, valable pour tous les ménages.",
  },
  {
    profil: 'Supérieurs',
    color: 'rose',
    description:
      "Majoration minimale ou identique au profil Intermédiaires selon les opérations. Le Coup de pouce reste accessible.",
  },
];

// ════════════════════════════════════════════════════════════════
// CONDITIONS
// ════════════════════════════════════════════════════════════════

export const COUP_CONDITIONS: CoupCondition[] = [
  {
    iconName: 'home',
    title: 'Résidence principale ou secondaire',
    description:
      "Comme pour la prime énergie classique, aucune restriction sur le type de résidence (sauf mention contraire dans l'arrêté concerné).",
  },
  {
    iconName: 'file-signature',
    title: 'Engagement avant le devis',
    description:
      "Condition essentielle du dispositif : l'engagement de prime signé avec un partenaire agréé doit dater d'avant la signature du devis avec l'artisan.",
  },
  {
    iconName: 'hard-hat',
    title: 'Professionnel certifié',
    description:
      "L'artisan doit être certifié pour la catégorie de travaux concernée. Vérifiez sa certification avant signature.",
  },
  {
    iconName: 'shield-check',
    title: 'Partenaire signataire de la charte',
    description:
      "La prime majorée est versée uniquement via un acteur ayant signé la charte qualité correspondante (fournisseur d'énergie ou intermédiaire agréé).",
  },
];

// ════════════════════════════════════════════════════════════════
// DÉMARCHES
// ════════════════════════════════════════════════════════════════

export const COUP_DEMARCHES: CoupDemarche[] = [
  {
    step: 1,
    iconName: 'calculator',
    title: "Vérifier l'éligibilité de votre projet",
    description:
      "On vérifie si votre projet entre dans un Coup de pouce actif (Chauffage, Isolation, Rénovation performante). Le simulateur intègre les majorations du moment et chiffre une estimation.",
  },
  {
    step: 2,
    iconName: 'file-signature',
    title: 'Choisir un partenaire signataire de la charte',
    description:
      "Le bonus est versé via un acteur signataire de la charte qualité officielle. Solution Éco France est signataire et peut vous accompagner.",
  },
  {
    step: 3,
    iconName: 'hard-hat',
    title: 'Faire réaliser les travaux par un professionnel certifié',
    description:
      "L'artisan doit être certifié pour la catégorie de travaux. Si vous passez par notre réseau d'artisans partenaires, la vérification est automatique.",
  },
  {
    step: 4,
    iconName: 'file-up',
    title: 'Constituer le dossier',
    description:
      "Devis, facture, attestation, photos avant/après selon l'opération. Le dossier est déposé sur le registre officiel par votre partenaire.",
  },
  {
    step: 5,
    iconName: 'wallet',
    title: 'Recevoir la prime majorée',
    description:
      "La prime est versée 30 à 60 jours après dépôt et validation. Avec notre réseau d'artisans, la prime est directement déduite du devis (zéro avance).",
  },
];

// ════════════════════════════════════════════════════════════════
// CUMUL
// ════════════════════════════════════════════════════════════════

export const COUP_CUMULS: CoupCumul[] = [
  {
    aide: "MaPrimeRénov'",
    iconName: 'piggy-bank',
    description:
      "Cumul direct : l'aide de l'État + Coup de pouce = combo classique pour les ménages modestes.",
    tone: 'brand',
    href: '/particuliers/aides/maprimerenov',
  },
  {
    aide: 'Prime énergie de base',
    iconName: 'sparkles',
    description:
      "Le Coup de pouce remplace la prime classique sur l'opération concernée (ce n'est pas un cumul, mais un remplacement avantageux).",
    tone: 'accent',
    href: '/particuliers/aides/cee',
  },
  {
    aide: 'Éco-PTZ',
    iconName: 'banknote',
    description:
      'Le prêt à taux zéro finance le reste à charge après aides. Compatible avec les Coups de pouce sans restriction.',
    tone: 'sage',
    href: '/particuliers/aides/eco-ptz',
  },
  {
    aide: 'TVA à 5,5 %',
    iconName: 'percent',
    description:
      "Le taux réduit s'applique sur la facture, indépendamment des Coups de pouce. Économie automatique.",
    tone: 'lavender',
    href: '/particuliers/aides/tva-reduite',
  },
];

// ════════════════════════════════════════════════════════════════
// FAQ
// ════════════════════════════════════════════════════════════════

export const COUP_FAQ: CoupFaqItem[] = [
  {
    question: 'Quelle différence entre Coup de pouce et prime énergie classique ?',
    answer:
      "La prime classique couvre toute opération éligible au tarif standard. Le Coup de pouce cible certaines opérations prioritaires (remplacement chauffage fossile, isolation des combles, rénovation performante) avec un montant majoré, surtout pour les ménages modestes.",
  },
  {
    question: 'Quels Coups de pouce sont disponibles en 2026 ?',
    answer:
      "Les principaux : Coup de pouce Chauffage (remplacement d'un chauffage fossile), Coup de pouce Isolation (combles, rampants, planchers) et Coup de pouce Rénovation performante (rénovation globale avec un gain énergétique important). La liste évolue régulièrement.",
  },
  {
    question: "Le Coup de pouce est-il cumulable avec l'aide de l'État ?",
    answer:
      "Oui, intégralement. Le Coup de pouce est une majoration de la prime énergie, il se cumule avec l'aide de l'État. Pour les ménages très modestes, le cumul peut couvrir une part très importante du coût des travaux.",
  },
  {
    question: "Tous les artisans peuvent-ils participer ?",
    answer:
      "L'artisan doit être certifié pour la catégorie de travaux. Il doit aussi accepter les conditions imposées par la charte Coup de pouce. Vous êtes libre de choisir votre artisan parmi ceux qui correspondent à ces critères.",
  },
  {
    question: "Pourquoi faut-il signer l'engagement avant le devis ?",
    answer:
      "C'est une condition commune à tout le dispositif de prime énergie. L'engagement signé avec un partenaire agréé doit dater d'avant la signature du devis avec l'artisan. Sinon, le dossier est rejeté. C'est la première cause de refus.",
  },
  {
    question: 'Existe-t-il un Coup de pouce pour les copropriétés ?',
    answer:
      "Les copropriétés bénéficient de primes collectives, qui peuvent intégrer des majorations du moment sur des opérations communes (chauffage collectif, isolation des parties communes). Le terme officiel est « primes collectives ». Voir notre offre dédiée /coproprietes.",
  },
  {
    question: 'Combien puis-je recevoir avec un Coup de pouce ?',
    answer:
      "Le montant exact dépend de trois éléments : l'opération, votre région et votre profil de revenus. Les ménages très modestes et modestes touchent les majorations les plus fortes. Notre simulateur croise ces paramètres et chiffre l'estimation.",
  },
  {
    question: 'Solution Éco France est-elle signataire de la charte ?',
    answer:
      "Oui, Solution Éco France est signataire des chartes Coup de pouce applicables aux opérations de notre catalogue. Nous montons et déposons les dossiers de majoration pour notre réseau d'artisans partenaires, et nous pouvons être intermédiaire pour des artisans tiers qui nous le demandent.",
  },
];
